import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { StarIcon, QuoteIcon } from 'lucide-react';
export function Reviews() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.15,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section id="reviews" className="w-full bg-[#E8F5F3] py-24 sm:py-32" ref={ref} data-id="element-349">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-350">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-351">
          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-center justify-center gap-3 mb-6" data-id="element-352">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-353">◆</span>
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="w-16 h-0.5 bg-[#D4A853] origin-center" data-id="element-354" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-355">◆</span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-id="element-356">
            What Our Travelers Say
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-slate-500 text-lg max-w-xl mx-auto font-light" data-id="element-357">
            Stories from those who've experienced Nepal with us
          </motion.p>
        </div>

        {/* Testimonial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-358">
          {/* Review 1 */}
          <motion.article custom={0} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="bg-white backdrop-blur rounded-2xl p-8 relative border border-teal-100 shadow-sm" data-id="element-359">
            <QuoteIcon className="w-10 h-10 text-teal-200 mb-4" data-id="element-360" />
            <div className="flex items-center gap-1 mb-4" data-id="element-361">
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-362" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-363" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-364" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-365" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-366" />
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 italic" data-id="element-367">
              "An absolutely transformative experience. From the moment we
              landed in Kathmandu, every detail was taken care of. The trek to
              Everest Base Camp was challenging yet luxurious — something I
              didn't think was possible. Our guide Pemba was extraordinary."
            </p>
            <div className="flex items-center gap-4" data-id="element-368">
              <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold text-sm" data-id="element-369">
                SM
              </div>
              <div data-id="element-370">
                <p className="font-semibold text-slate-900" data-id="element-371">Sarah Mitchell</p>
                <p className="text-sm text-slate-500" data-id="element-372">
                  Everest Base Camp, 2024
                </p>
              </div>
            </div>
          </motion.article>

          {/* Review 2 */}
          <motion.article custom={1} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="bg-white backdrop-blur rounded-2xl p-8 relative border border-teal-100 shadow-sm" data-id="element-373">
            <QuoteIcon className="w-10 h-10 text-teal-200 mb-4" data-id="element-374" />
            <div className="flex items-center gap-1 mb-4" data-id="element-375">
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-376" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-377" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-378" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-379" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-380" />
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 italic" data-id="element-381">
              "Nepal Luxury Travel exceeded every expectation we had. The
              Annapurna Circuit was breathtaking, and the accommodations along
              the way were far beyond what we imagined. A honeymoon we will
              never forget. The cultural experiences were deeply moving."
            </p>
            <div className="flex items-center gap-4" data-id="element-382">
              <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold text-sm" data-id="element-383">
                JP
              </div>
              <div data-id="element-384">
                <p className="font-semibold text-slate-900" data-id="element-385">
                  James & Emily Parker
                </p>
                <p className="text-sm text-slate-500" data-id="element-386">
                  Annapurna Circuit, 2024
                </p>
              </div>
            </div>
          </motion.article>

          {/* Review 3 */}
          <motion.article custom={2} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="bg-white backdrop-blur rounded-2xl p-8 relative border border-teal-100 shadow-sm" data-id="element-387">
            <QuoteIcon className="w-10 h-10 text-teal-200 mb-4" data-id="element-388" />
            <div className="flex items-center gap-1 mb-4" data-id="element-389">
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-390" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-391" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-392" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-393" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-394" />
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 italic" data-id="element-395">
              "The attention to detail was remarkable. From the private jeep
              safaris to the candlelit dinners overlooking the jungle, Chitwan
              was pure magic. The team's knowledge of wildlife and conservation
              efforts was truly inspiring."
            </p>
            <div className="flex items-center gap-4" data-id="element-396">
              <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold text-sm" data-id="element-397">
                RP
              </div>
              <div data-id="element-398">
                <p className="font-semibold text-slate-900" data-id="element-399">Dr. Raj Patel</p>
                <p className="text-sm text-slate-500" data-id="element-400">Chitwan Safari, 2025</p>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}